import { FIELD_LABELS, prettyValue, validateValue, type VerifiableField } from '@core/verification'

/**
 * One extracted field.
 *
 * The coloured bar on the left is the validation state, not the confidence
 * score — a human should never be shown "0.87" and asked to interpret it.
 * Green means a deterministic check passed (the IBAN's checksum, the
 * reference's mod-97). Amber means look. Red means it is provably wrong.
 */
export function FieldRow({
  field,
  value,
  active,
  editing,
  onSelect,
  onEdit,
  onChange,
}: {
  field: VerifiableField
  value: string
  active: boolean
  editing: boolean
  onSelect: () => void
  onEdit: () => void
  onChange: (v: string) => void
}) {
  const check = validateValue(field.name, value)
  const changed = value !== field.value

  const tone = !check.ok
    ? 'bg-danger'
    : field.validation === 'fail'
      ? 'bg-danger'
      : field.confidence < 0.85
        ? 'bg-warn'
        : 'bg-ok'

  /**
   * One row, one action.
   *
   * This used to be a `div` with an onClick that selected the field, wrapping a
   * `button` that stopped propagation and started editing. Two overlapping
   * targets with different outcomes, no line between them, and — because the
   * outer one was a div — no pressed state at all. Tapping "somewhere on the
   * row" did one of two things depending on a few millimetres, which is
   * indistinguishable from a screen that ignores you.
   *
   * You are on this screen to correct what the machine read. So the whole row
   * is one button, and it edits. Selecting the field (which draws the box on
   * the document above) happens on the same tap, because wanting to look at a
   * number and wanting to fix it are the same moment.
   */
  return (
    <div
      className={`flex gap-3 rounded-xl border transition-colors ${
        active ? 'border-ink bg-surface' : 'border-transparent'
      }`}
    >
      <span className={`w-1 my-2 rounded-full shrink-0 ${tone}`} aria-hidden />

      <div className="flex-1 min-w-0 pr-3">
        {editing ? (
          <div className="px-0 py-2">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-xs text-muted">{FIELD_LABELS[field.name] ?? field.name}</span>
              {changed && <span className="text-xs text-ok">aangepast</span>}
            </div>
            <input
              autoFocus
              value={value}
              onChange={(e) => onChange(e.target.value)}
              inputMode={field.name.includes('amount') ? 'decimal' : 'text'}
              className="w-full mt-0.5 bg-transparent text-lg font-medium border-b border-ink
                         focus:outline-none py-1"
            />
          </div>
        ) : (
          <button
            onClick={() => {
              onSelect()
              onEdit()
            }}
            // min-h-[3rem]: a 48px target. The old one was the height of the
            // text plus 2px, which on a phone is a coin toss.
            className="block w-full text-left min-h-[3rem] py-2"
          >
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-xs text-muted">{FIELD_LABELS[field.name] ?? field.name}</span>
              {changed && <span className="text-xs text-ok">aangepast</span>}
            </div>
            <span className="block text-lg font-medium truncate">
              {prettyValue(field.name, value) || <span className="text-muted">— leeg —</span>}
            </span>
          </button>
        )}

        {!check.ok && <p className="text-xs text-danger pb-2">{check.note}</p>}
        {check.ok && field.validationNote && field.validation === 'fail' && (
          <p className="text-xs text-warn pb-2">{field.validationNote}</p>
        )}
      </div>
    </div>
  )
}
